import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getReviewsByUser,
    updateReview,
    deleteReview
} from '../../services/reviewService';
import { getPoojariById } from '../../services/poojariService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const keys = ['data', 'content', 'result', 'items'];
    for (const k of keys) {
        if (Array.isArray(payload[k])) return payload[k];
    }
    return [];
};

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} `
        + `${d.getFullYear()}`;
};

const MyReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [poojariNames, setPoojariNames] = useState({});
    const [loading, setLoading] = useState(true);

    // Edit modal
    const [editReview, setEditReview] = useState(null);
    const [editForm, setEditForm] = useState({
        rating: 5,
        title: '',
        comment: ''
    });
    const [saving, setSaving] = useState(false);

    // Delete confirm
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const { token, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        const timer = setTimeout(() => {
            fetchReviews();
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const userId = user?.id;
            if (!userId) {
                toast.error('Session expired. Login again.');
                navigate('/login');
                return;
            }

            const data = await getReviewsByUser(userId);

            // Sirf apne diye reviews - poojari ke nahi
            const mine = normalizeList(data)
                .filter(r =>
                    r.reviewType === 'USER_TO_POOJARI')
                .sort((a, b) => (b.id || 0) - (a.id || 0));

            setReviews(mine);
            loadPoojariNames(mine);

        } catch (error) {
            console.log('Error fetching reviews');
            setReviews([]);
        } finally {
            setLoading(false);
        }
    };

    // Har poojari ka naam le aao
    const loadPoojariNames = async (list) => {
        const ids = [...new Set(list.map(r => r.poojariId))];

        for (const id of ids) {
            try {
                const p = await getPoojariById(id);
                setPoojariNames(prev => ({
                    ...prev,
                    [id]: `${p.firstName} ${p.lastName}`
                }));
            } catch (err) {
                console.log('Could not load poojari', id);
            }
        }
    };

    const openEdit = (r) => {
        setEditReview(r);
        setEditForm({
            rating: r.rating,
            title: r.title || '',
            comment: r.comment || ''
        });
    };

    const confirmEdit = async (e) => {
        e.preventDefault();
        if (!editForm.title.trim()) {
            return toast.warning('Please add a title');
        }
        setSaving(true);
        try {
            await updateReview(editReview.id, {
                rating: parseInt(editForm.rating),
                title: editForm.title,
                comment: editForm.comment
            });
            toast.success('Review updated');
            setEditReview(null);
            fetchReviews();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not update review');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await deleteReview(deleteId);
            toast.success('Review deleted');
            setDeleteId(null);
            fetchReviews();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not delete review');
        } finally {
            setDeleting(false);
        }
    };

    const stars = (n) => '⭐'.repeat(n);

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: RED }}></div>
        </div>
    );

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen">

            {/* ===== HEADER ===== */}
            <div style={{
                backgroundColor: DARK_RED,
                padding: '14px 16px'
            }}>
                <div className="d-flex align-items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: GOLD,
                            fontSize: '22px',
                            lineHeight: 1,
                            padding: 0
                        }}>
                        ←
                    </button>
                    <div>
                        <p className="mb-0 fw-bold"
                            style={{
                                color: GOLD,
                                fontSize: '15px'
                            }}>
                            ⭐ My Reviews
                        </p>
                        <p className="mb-0"
                            style={{
                                color: '#FFE4B5',
                                fontSize: '12px'
                            }}>
                            {reviews.length} review(s) written
                        </p>
                    </div>
                </div>
            </div>

            <div className="container"
                style={{ maxWidth: '560px' }}>
                <div className="py-3">

                    {reviews.length === 0 ? (
                        <div className="text-center py-5">
                            <h6 style={{ color: DARK_RED }}>
                                No reviews yet
                            </h6>
                            <p className="text-muted"
                                style={{ fontSize: '14px' }}>
                                After a pooja is complete, you
                                can rate your poojari
                            </p>
                            <button
                                className="btn fw-bold mt-2"
                                style={{
                                    backgroundColor: RED,
                                    color: GOLD,
                                    border: 'none'
                                }}
                                onClick={() =>
                                    navigate('/home',
                                        { state: {
                                            tab: 'history'
                                        }})}>
                                Go to my bookings
                            </button>
                        </div>
                    ) : (
                        <div className="row g-2">
                            {reviews.map(r => (
                                <div key={r.id}
                                    className="col-12">
                                    <div className="card"
                                        style={{
                                            borderRadius: '12px',
                                            border: `1px solid ${GOLD}`
                                        }}>
                                        <div className="card-body p-3">

                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div style={{
                                                    minWidth: 0
                                                }}>
                                                    <p className="mb-0 fw-bold text-truncate"
                                                        style={{
                                                            color: DARK_RED,
                                                            fontSize: '14px'
                                                        }}>
                                                        {poojariNames[
                                                            r.poojariId]
                                                            || 'Poojari'}
                                                    </p>
                                                    <p className="mb-0 text-muted"
                                                        style={{
                                                            fontSize: '12px'
                                                        }}>
                                                        Booking #{r.bookingId}
                                                        {' · '}
                                                        {formatDate(
                                                            r.createdAt)}
                                                    </p>
                                                </div>
                                                <span className="flex-shrink-0 ms-2"
                                                    style={{
                                                        fontSize: '13px'
                                                    }}>
                                                    {stars(r.rating)}
                                                </span>
                                            </div>

                                            <div className="p-2 mb-2"
                                                style={{
                                                    backgroundColor:
                                                        '#FFF8F8',
                                                    borderRadius: '8px'
                                                }}>
                                                <p className="mb-1 fw-bold"
                                                    style={{
                                                        color: DARK_RED,
                                                        fontSize: '13px'
                                                    }}>
                                                    {r.title}
                                                </p>
                                                {r.comment && (
                                                    <p className="mb-0 text-muted"
                                                        style={{
                                                            fontSize: '13px'
                                                        }}>
                                                        {r.comment}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm fw-bold"
                                                    style={{
                                                        border: `1px solid ${RED}`,
                                                        color: RED,
                                                        backgroundColor:
                                                            'transparent',
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={() =>
                                                        openEdit(r)}>
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    style={{
                                                        fontSize: '12px'
                                                    }}
                                                    onClick={() =>
                                                        setDeleteId(
                                                            r.id)}>
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ===== EDIT MODAL ===== */}
            {editReview && (
                <>
                    <div onClick={() => setEditReview(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '460px', maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Edit your review
                            </div>
                            <div className="card-body p-4">
                                <p className="text-muted mb-3"
                                    style={{ fontSize: '13px' }}>
                                    {poojariNames[
                                        editReview.poojariId]
                                        || 'Poojari'}
                                </p>

                                <form onSubmit={confirmEdit}>

                                    <div className="mb-3 text-center">
                                        <label className="form-label fw-bold d-block"
                                            style={{
                                                color: DARK_RED
                                            }}>
                                            Your Rating
                                        </label>
                                        <div>
                                            {[1, 2, 3, 4, 5].map(
                                                star => (
                                                <span
                                                    key={star}
                                                    onClick={() =>
                                                        setEditForm({
                                                            ...editForm,
                                                            rating: star
                                                        })}
                                                    style={{
                                                        fontSize: '34px',
                                                        cursor: 'pointer',
                                                        marginRight: '4px',
                                                        filter: star <= editForm.rating
                                                            ? 'none'
                                                            : 'grayscale(100%)',
                                                        opacity: star <= editForm.rating
                                                            ? 1 : 0.35
                                                    }}>
                                                    ⭐
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold"
                                            style={{
                                                color: DARK_RED
                                            }}>
                                            Title
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.title}
                                            onChange={e =>
                                                setEditForm({
                                                    ...editForm,
                                                    title: e.target.value
                                                })}
                                            required
                                            style={{
                                                borderColor: GOLD
                                            }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-bold"
                                            style={{
                                                color: DARK_RED
                                            }}>
                                            Your Review
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={editForm.comment}
                                            onChange={e =>
                                                setEditForm({
                                                    ...editForm,
                                                    comment: e.target.value
                                                })}
                                            style={{
                                                borderColor: GOLD
                                            }}>
                                        </textarea>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary flex-fill"
                                            onClick={() =>
                                                setEditReview(null)}
                                            disabled={saving}>
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn flex-fill fw-bold"
                                            style={{
                                                backgroundColor: RED,
                                                color: GOLD
                                            }}
                                            disabled={saving}>
                                            {saving
                                                ? 'Saving...'
                                                : 'Save changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== DELETE CONFIRM ===== */}
            {deleteId && (
                <>
                    <div onClick={() => setDeleteId(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '400px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-body p-4">
                                <p className="mb-3"
                                    style={{
                                        fontSize: '15px',
                                        color: DARK_RED,
                                        fontWeight: 600
                                    }}>
                                    Delete this review?
                                </p>
                                <p className="text-muted mb-4"
                                    style={{ fontSize: '13px' }}>
                                    The poojari's rating will be
                                    recalculated. This cannot
                                    be undone.
                                </p>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary flex-fill"
                                        onClick={() =>
                                            setDeleteId(null)}
                                        disabled={deleting}>
                                        Keep it
                                    </button>
                                    <button
                                        className="btn btn-danger flex-fill fw-bold"
                                        onClick={confirmDelete}
                                        disabled={deleting}>
                                        {deleting
                                            ? 'Deleting...'
                                            : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MyReviews;